import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { avvTemplateData, SavedAVV, AVVContractParty, AVVProjectDetails, getIncompleteAVVFields } from '@/lib/constants/avvTemplateData';
import { v4 as uuidv4 } from 'uuid';

interface AVVFormProps {
  existingAVV: SavedAVV | null;
  onSave: (avv: SavedAVV) => void;
  onCancel: () => void;
}

interface FormData {
  controllerName: string;
  controllerAddress: string;
  controllerRepresentedBy: string;
  controllerDPO: string;
  controllerDPOEmail: string;
  controllerDPOPhone: string;
  projectName: string;
  projectDescription: string;
  contractStart: string;
  contractEnd: string;
  dataSubjectCategories: string;
  personalDataCategories: string;
  processingPurposes: string;
  mainContractReference: string;
}

const AVVForm: React.FC<AVVFormProps> = ({ existingAVV, onSave, onCancel }) => {
  const [incompleteFields, setIncompleteFields] = useState<string[]>(() => {
    if (existingAVV) return getIncompleteAVVFields(existingAVV);
    return [];
  });

  const defaults = existingAVV
    ? {
        controllerName: existingAVV.controller.name,
        controllerAddress: existingAVV.controller.address,
        controllerRepresentedBy: existingAVV.controller.representedBy,
        controllerDPO: existingAVV.controller.dataProtectionOfficer,
        controllerDPOEmail: existingAVV.controller.dpoEmail,
        controllerDPOPhone: existingAVV.controller.dpoPhone,
        projectName: existingAVV.project.projectName,
        projectDescription: existingAVV.project.projectDescription,
        contractStart: existingAVV.project.contractStart,
        contractEnd: existingAVV.project.contractEnd,
        mainContractReference: existingAVV.project.mainContractReference || '',
        dataSubjectCategories: existingAVV.project.dataSubjectCategories.join('\n'),
        personalDataCategories: existingAVV.project.personalDataCategories.join('\n'),
        processingPurposes: existingAVV.project.processingPurposes.join('\n'),
      }
    : {
        controllerName: avvTemplateData.defaultController.name,
        controllerAddress: avvTemplateData.defaultController.address,
        controllerRepresentedBy: avvTemplateData.defaultController.representedBy,
        controllerDPO: avvTemplateData.defaultController.dataProtectionOfficer,
        controllerDPOEmail: avvTemplateData.defaultController.dpoEmail,
        controllerDPOPhone: avvTemplateData.defaultController.dpoPhone,
        projectName: avvTemplateData.defaultProject.projectName,
        projectDescription: avvTemplateData.defaultProject.projectDescription,
        contractStart: avvTemplateData.defaultProject.contractStart,
        contractEnd: avvTemplateData.defaultProject.contractEnd,
        mainContractReference: avvTemplateData.defaultProject.mainContractReference,
        dataSubjectCategories: avvTemplateData.defaultProject.dataSubjectCategories.join('\n'),
        personalDataCategories: avvTemplateData.defaultProject.personalDataCategories.join('\n'),
        processingPurposes: avvTemplateData.defaultProject.processingPurposes.join('\n'),
      };

  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: defaults,
  });

  const buildAVV = (data: FormData): SavedAVV => {
    const controller: AVVContractParty = {
      name: data.controllerName,
      address: data.controllerAddress,
      representedBy: data.controllerRepresentedBy,
      dataProtectionOfficer: data.controllerDPO,
      dpoEmail: data.controllerDPOEmail,
      dpoPhone: data.controllerDPOPhone,
    };

    const project: AVVProjectDetails = {
      projectName: data.projectName,
      projectDescription: data.projectDescription,
      contractStart: data.contractStart,
      contractEnd: data.contractEnd,
      mainContractReference: data.mainContractReference,
      dataSubjectCategories: data.dataSubjectCategories.split('\n').filter(s => s.trim()),
      personalDataCategories: data.personalDataCategories.split('\n').filter(s => s.trim()),
      processingPurposes: data.processingPurposes.split('\n').filter(s => s.trim()),
    };

    const now = new Date().toISOString();
    return {
      id: existingAVV?.id || uuidv4(),
      controller,
      project,
      createdAt: existingAVV?.createdAt || now,
      updatedAt: now,
      status: existingAVV?.status || 'draft',
    };
  };

  const onSubmit = (data: FormData) => {
    const avv = buildAVV(data);
    const missing = getIncompleteAVVFields(avv);
    setIncompleteFields(missing);
    onSave(avv);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück
        </Button>
        <h1 className="text-2xl font-bold">
          {existingAVV ? 'AVV bearbeiten' : 'Neuen AVV erstellen'}
        </h1>
      </div>

      {incompleteFields.length > 0 && (
        <Alert variant="destructive" className="border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-700 [&>svg]:text-yellow-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unvollständiger Entwurf</AlertTitle>
          <AlertDescription>
            Folgende Angaben fehlen noch: {incompleteFields.join(', ')}.
            Sie können den Entwurf trotzdem speichern und die fehlenden Angaben später ergänzen.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Auftraggeber */}
        <Card>
          <CardHeader>
            <CardTitle>Auftraggeber (Verantwortlicher)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="controllerName">Organisation</Label>
                <Input id="controllerName" {...register('controllerName')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="controllerAddress">Anschrift</Label>
                <Input id="controllerAddress" {...register('controllerAddress')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="controllerRepresentedBy">Vertretungsberechtigte Person</Label>
                <Input id="controllerRepresentedBy" {...register('controllerRepresentedBy')} />
              </div>
            </div>

            <Separator />
            <h4 className="font-medium">Datenschutzbeauftragter</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="controllerDPO">Name</Label>
                <Input id="controllerDPO" {...register('controllerDPO')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="controllerDPOEmail">E-Mail</Label>
                <Input id="controllerDPOEmail" type="text" {...register('controllerDPOEmail')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="controllerDPOPhone">Telefon</Label>
                <Input id="controllerDPOPhone" {...register('controllerDPOPhone')} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projekt */}
        <Card>
          <CardHeader>
            <CardTitle>Projektdetails</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="projectName">Projektbezeichnung</Label>
              <Input id="projectName" {...register('projectName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="projectDescription">Projektbeschreibung</Label>
              <Textarea id="projectDescription" rows={3} {...register('projectDescription')} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contractStart">Vertragsbeginn</Label>
                <Input id="contractStart" type="date" {...register('contractStart')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contractEnd">Vertragsende</Label>
                <Input id="contractEnd" type="date" {...register('contractEnd')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mainContractReference">Verweis auf den Hauptvertrag</Label>
              <Input
                id="mainContractReference"
                placeholder="z.B. Kooperationsvertrag vom 01.03.2025 zwischen Fraunhofer ENAS und PaperVerse UG"
                {...register('mainContractReference')}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Wird in § 1 des AVV als Definition des Hauptvertrags eingefügt.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Datenverarbeitung */}
        <Card>
          <CardHeader>
            <CardTitle>Datenverarbeitungsdetails</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dataSubjectCategories">Kategorien betroffener Personen (eine pro Zeile)</Label>
              <Textarea id="dataSubjectCategories" rows={4} {...register('dataSubjectCategories')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="personalDataCategories">Arten personenbezogener Daten (eine pro Zeile)</Label>
              <Textarea id="personalDataCategories" rows={5} {...register('personalDataCategories')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="processingPurposes">Verarbeitungszwecke (einer pro Zeile)</Label>
              <Textarea id="processingPurposes" rows={5} {...register('processingPurposes')} />
            </div>
          </CardContent>
        </Card>

        {/* Auftragnehmer-Info (read-only) */}
        <Card>
          <CardHeader>
            <CardTitle>Auftragnehmer (Auftragsverarbeiter)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4 space-y-1 text-sm">
              <p><strong>{avvTemplateData.processor.name}</strong></p>
              <p>{avvTemplateData.processor.address}</p>
              <p>Vertreten durch: {avvTemplateData.processor.representedBy}</p>
              <p>DSB: {avvTemplateData.processor.dataProtectionOfficer} ({avvTemplateData.processor.dpoEmail})</p>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Diese Daten werden in der Konfiguration verwaltet und sind hier nicht editierbar.
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button type="submit">
            <Save className="h-4 w-4 mr-2" />
            {existingAVV ? 'Änderungen speichern' : 'AVV erstellen'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AVVForm;
